import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ENGINE_STEPS } from "@/lib/constants";
import { Loader2, Check } from "lucide-react";

/**
 * Staged animation. Advances every ~1.6s, holds the last step until `done` is true.
 */
export default function EngineAnimation({ done, error }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (done || error) return;
    const id = setInterval(() => {
      setStep((s) => Math.min(s + 1, ENGINE_STEPS.length - 1));
    }, 1600);
    return () => clearInterval(id);
  }, [done, error]);

  useEffect(() => {
    if (done) setStep(ENGINE_STEPS.length - 1);
  }, [done]);

  return (
    <div
      data-testid="engine-animation"
      className="rounded-2xl border border-white/10 bg-[#12181A] p-8 sm:p-10"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
        Engine
      </div>
      <div className="mt-3 flex items-baseline gap-3">
        <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Thinking through your problem
        </h2>
      </div>
      <p className="mt-2 text-sm text-[#A0AAB2] max-w-xl leading-relaxed">
        Same pipeline a top strategy team would run. Just faster.
      </p>

      <ul className="mt-8 space-y-3">
        {ENGINE_STEPS.map((label, i) => {
          const isActive = i === step && !done && !error;
          const isDone = i < step || done;
          return (
            <li
              key={label}
              data-testid={`engine-step-${i}`}
              className="flex items-center gap-3"
            >
              <span
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition-[background-color,border-color,color] duration-300 ${
                  isDone
                    ? "bg-[#35D07F] border-[#35D07F] text-black"
                    : isActive
                    ? "bg-transparent border-[#35D07F]/60 text-[#35D07F]"
                    : "bg-transparent border-white/10 text-white/30"
                }`}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isDone ? (
                    <motion.span
                      key="done"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </motion.span>
                  ) : isActive ? (
                    <motion.span
                      key="active"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="idle"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-[10px] font-mono"
                    >
                      {i + 1}
                    </motion.span>
                  )}
                </AnimatePresence>
              </span>
              <span
                className={`text-sm ${
                  isDone
                    ? "text-white"
                    : isActive
                    ? "text-white"
                    : "text-white/40"
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>

      {error ? (
        <div className="mt-8 rounded-lg border border-[#FF5252]/40 bg-[#FF5252]/10 p-4 text-sm text-[#FFB4B4]">
          Something went wrong. {error}
        </div>
      ) : null}
    </div>
  );
}
